import { library } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslator } from '@Localization';
import { AutocompleteOption, Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ValidatedSelectInput } from '../../../components/ValidatedSelectInput';
import { InputStatus } from '../../../models/error.model';
import { ITeamMember } from '../../team/models/TeamMember';
import { ITakeOverWorkspaceLimits } from '../workspaces.store';
import { LimitsValidation } from './LimitsValidation';

library.add(faInfoCircle);

interface IChangeWorkspaceOwnerModalProps {
  showChangeOwner: boolean;
  users: ITeamMember[];
  ownerEmail: string;
  ownerEmailStatus: InputStatus;
  takeoverWorkspaceLimits?: ITakeOverWorkspaceLimits;
  onOwnerEmailChange: (email: string) => Promise<void>;
  toggleOwnerModal: () => void;
  onOwnerSave: () => Promise<void>;
}

export const ChangeWorkspaceOwnerModal = observer((props: IChangeWorkspaceOwnerModalProps): ReactElement => {
  const { t } = useTranslator();

  return (
    <S_Modal
      isOpen={props.showChangeOwner}
      toggle={props.toggleOwnerModal}
      headerText={t('change_workspace_owner')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={props.toggleOwnerModal}
          >
            {t('workspace_cancel')}
          </Button>
          <ButtonWithLoader
            loaderKey='change-workspace-owner-button'
            disabled={
              props.ownerEmailStatus.isValid !== true ||
              props.ownerEmail === '' ||
              props.takeoverWorkspaceLimits === undefined ||
              !props.takeoverWorkspaceLimits.isAllowed
            }
            onClick={props.onOwnerSave}
          >
            {t('change_owner')}
          </ButtonWithLoader>
        </>
      }
    >
      <ValidatedSelectInput
        id='user-dropdown'
        name='select'
        value={props.users.find((x) => x.email === props.ownerEmail)}
        placeholder={t('email_address')}
        onChange={(_, v) => {
          void props.onOwnerEmailChange(v.email ?? '');
        }}
        isValid={props.ownerEmailStatus.isValid}
        feedbacks={props.ownerEmailStatus.errorCodes}
        getOptionDisabled={(u) => !u.hasAccount}
        getOptionLabel={(u) => u.email}
        options={props.users}
        renderOption={(props, u) => (
          <AutocompleteOption {...props}>
            <div title={!u.hasAccount ? t('this_user_has_not_registered_yet') : ''}>{u.email}</div>
          </AutocompleteOption>
        )}
      />
      <div className='alerts'>
        <div className='alerts-info'>{t('owner_change_info')}</div>
      </div>
      {props.takeoverWorkspaceLimits !== undefined && <LimitsValidation {...props.takeoverWorkspaceLimits} />}
    </S_Modal>
  );
});
